// 지점 내 승급 공지 + CP 대량 획득 공지를 5초마다 폴링하여 큐에 추가하는 훅
import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { API_ENDPOINTS } from "../constants/apiEndpoints";

export interface TierPromotionItem {
  kind: "tier";
  id: number;
  student_name: string;
  new_tier: string;
  new_level: number;
  tier_kr: string;
  level_label: string;
  is_placement: boolean;
  created_at: string;
}

export interface CPAcquisitionItem {
  kind: "cp";
  id: number;
  student_name: string;
  cp_amount: number;
  created_at: string;
}

export type NotificationItem = TierPromotionItem | CPAcquisitionItem;

export function useTierPromotion(enabled: boolean) {
  const [queue, setQueue] = useState<NotificationItem[]>([]);
  const seenTierIds = useRef<Set<number>>(new Set());
  const seenCpIds   = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled) return;

    const poll = async () => {
      try {
        const [tierRes, cpRes] = await Promise.all([
          axiosInstance.get<Omit<TierPromotionItem, "kind">[]>(
            API_ENDPOINTS.NOTIFICATIONS.TIER_PROMOTIONS
          ),
          axiosInstance.get<Omit<CPAcquisitionItem, "kind">[]>(
            API_ENDPOINTS.NOTIFICATIONS.CP_ACQUISITIONS
          ),
        ]);

        const freshItems: NotificationItem[] = [];

        for (const p of tierRes.data) {
          if (!seenTierIds.current.has(p.id)) {
            seenTierIds.current.add(p.id);
            freshItems.push({ kind: "tier", ...p });
          }
        }
        for (const c of cpRes.data) {
          if (!seenCpIds.current.has(c.id)) {
            seenCpIds.current.add(c.id);
            freshItems.push({ kind: "cp", ...c });
          }
        }

        if (freshItems.length > 0) {
          setQueue((prev) => [...prev, ...freshItems]);
        }
      } catch {
        // 네트워크 오류는 무시 — 다음 폴링에서 재시도
      }
    };

    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [enabled]);

  const dismissAll = useCallback(() => {
    setQueue((prev) => {
      const tierIds = prev.filter((n) => n.kind === "tier").map((n) => n.id);
      const cpIds   = prev.filter((n) => n.kind === "cp").map((n) => n.id);

      if (tierIds.length > 0) {
        axiosInstance
          .post(API_ENDPOINTS.NOTIFICATIONS.TIER_PROMOTIONS_READ, { ids: tierIds })
          .catch(() => {});
      }
      if (cpIds.length > 0) {
        axiosInstance
          .post(API_ENDPOINTS.NOTIFICATIONS.CP_ACQUISITIONS_READ, { ids: cpIds })
          .catch(() => {});
      }
      return [];
    });
  }, []);

  return { queue, dismissAll };
}
